import { useMemo } from 'react';
import {
  Cable,
  FlaskConical,
  Plug,
  Puzzle,
  ShieldUser,
  Sparkles,
  ShieldCheck,
  Building2,
} from 'lucide-react';
import { usePlugins } from '@/hooks/config/usePlugins';
import type { ResolvedNavItem } from '@/lib/plugin-registry';
import { useAuth } from '@/hooks/auth/useAuth';
import { useActiveOrgSlug } from '@/lib/useOrgRoutes';
import { orgBasePath, withOrgSlug, ROUTES } from '@/lib/routes';
import type { NavItem, NavDrillGroup } from './nav-types';
import {
  applyBottomRoutes,
  buildMainNavItems,
  buildCoreIntegrationNavItems,
  buildCoreMiscNavItems,
  buildCoreOrganizationNavItems,
  buildCoreSecurityNavItems,
  filterNavItemsByPermission,
} from './nav-items';

export interface NavSources {
  /** Main-group items (Sessions + main-group plugin contributions), permission-filtered and slug-scoped. */
  allMainNavItems: NavItem[];
  /**
   * Every visible drill group, including sub-drills (those with `parentBasePath`).
   * Required by `getActiveDrillGroup` URL resolution; not for top-level rendering.
   */
  visibleDrillGroups: NavDrillGroup[];
  /** Top-level drill groups positioned in the main column (work surfaces only). */
  mainDrillGroups: NavDrillGroup[];
  /** Top-level drill groups positioned in the bottom column (work surfaces only). */
  bottomDrillGroups: NavDrillGroup[];
  /**
   * Config drill groups that render inside the Configure takeover
   * (`ConfigureShell`) instead of the desktop sidebar. Includes sub-drills
   * (Integrations); consumers filter on `parentBasePath` as needed.
   */
  configureDrillGroups: NavDrillGroup[];
  /**
   * Drill groups that render inside the Labs takeover (`LabsShell`)
   * instead of the desktop sidebar: Misc and Plugins.
   */
  labsDrillGroups: NavDrillGroup[];
  /**
   * The Chat entry pulled out of the main items — Labs opens on it and its
   * shell pins it above the group lists. Null when the chat plugin is not
   * installed.
   */
  labsChatItem: NavItem | null;
  /**
   * The takeover doors clustered at the bottom of the sidebar, rendered
   * directly above the Configure entry: Automations, then Connect.
   */
  takeoverBottomItems: NavItem[];
  /** Plugin-contributed bottom items, permission-filtered. */
  pluginBottomItems: NavItem[];
  /**
   * The Labs entry: icon-only next to Admin on desktop, a labeled More-sheet
   * row on mobile. Navigates to the Labs takeover (Chat when installed).
   */
  labsItem: NavItem;
  /**
   * The Admin entry for the utility row, shown only to platform admins.
   * Navigates to the Admin takeover; null when hidden.
   */
  adminItem: NavItem | null;
  /** Raw plugin nav items with their `to` already scoped to the active org slug. */
  scopedPluginNavItems: ResolvedNavItem[];
}

/**
 * Canonical nav composition shared between the desktop sidebar (`NavBar`) and
 * the mobile bottom nav (`MobileBottomNav`).
 *
 * Centralising this composition means a new plugin nav contribution, a new
 * core drill group, or a permission tweak automatically surfaces in both
 * desktop and mobile without having to mirror the logic twice. The only piece
 * the mobile layout owns separately is the hand-curated bottom-bar slot list.
 */
export function useNavSources(): NavSources {
  const { navItems: pluginNavItems } = usePlugins();
  const { can, isPlatformAdmin, authEnabled } = useAuth();
  const slug = useActiveOrgSlug();

  const scopedPluginNavItems = useMemo<ResolvedNavItem[]>(
    () =>
      pluginNavItems.map((i) => ({
        ...i,
        to: i.rootLevel ? i.to : withOrgSlug(slug, i.to),
      })),
    [pluginNavItems, slug],
  );

  const mainNavItems = useMemo(() => buildMainNavItems(slug), [slug]);
  const coreIntegrationNavItems = useMemo(() => buildCoreIntegrationNavItems(), []);
  const coreMiscNavItems = useMemo(() => buildCoreMiscNavItems(slug), [slug]);
  const coreOrganizationNavItems = useMemo(() => buildCoreOrganizationNavItems(slug), [slug]);
  const coreSecurityNavItems = useMemo(() => buildCoreSecurityNavItems(slug), [slug]);

  const filterByPerm = useMemo(
    () =>
      <T>(items: readonly T[]): T[] =>
        authEnabled ? filterNavItemsByPermission(items, can, isPlatformAdmin) : [...items],
    [authEnabled, can, isPlatformAdmin],
  );

  // Chat lives inside the Labs takeover, not the main column; its main-group
  // nav item becomes the Labs entry's landing target instead. Automations
  // is a takeover door too — both cluster at the bottom of the sidebar,
  // above Configure, leaving the main column to Sessions.
  const chatTo = slug ? ROUTES.org(slug).chat : null;
  const automationsTo = slug ? ROUTES.org(slug).automations : null;
  const labsChatItem = useMemo<NavItem | null>(
    () =>
      filterByPerm(scopedPluginNavItems.filter((i) => i.group === 'main' && i.to === chatTo))[0] ??
      null,
    [scopedPluginNavItems, chatTo, filterByPerm],
  );
  const automationsItem = useMemo<NavItem | null>(
    () =>
      filterByPerm(
        scopedPluginNavItems.filter((i) => i.group === 'main' && i.to === automationsTo),
      )[0] ?? null,
    [scopedPluginNavItems, automationsTo, filterByPerm],
  );

  const allMainNavItems = useMemo<NavItem[]>(
    () =>
      filterByPerm([
        ...mainNavItems,
        ...scopedPluginNavItems.filter(
          (i) => i.group === 'main' && i.to !== chatTo && i.to !== automationsTo,
        ),
      ]),
    [mainNavItems, scopedPluginNavItems, chatTo, automationsTo, filterByPerm],
  );

  const drillGroups = useMemo<NavDrillGroup[]>(() => {
    const dedup = (core: NavItem[], plugin: NavItem[]): NavItem[] => {
      const seen = new Set(core.map((i) => i.to));
      return [...core, ...plugin.filter((i) => !seen.has(i.to))];
    };
    const slugPrefix = slug ? orgBasePath(slug) : '';
    return [
      {
        label: 'Plugins',
        icon: Puzzle,
        basePath: `${slugPrefix}/plugins`,
        items: filterByPerm(scopedPluginNavItems.filter((i) => i.group === 'plugins')),
        position: 'main',
        labsSurface: true,
      },
      {
        label: 'Security',
        icon: ShieldCheck,
        basePath: `${slugPrefix}/security`,
        tutorialId: 'nav-security',
        items: filterByPerm(
          dedup(
            coreSecurityNavItems,
            scopedPluginNavItems.filter((i) => i.group === 'security'),
          ),
        ),
        position: 'main',
        configureSurface: true,
      },
      {
        label: 'Misc',
        icon: Sparkles,
        basePath: `${slugPrefix}/misc`,
        items: filterByPerm(
          dedup(
            coreMiscNavItems,
            scopedPluginNavItems.filter((i) => i.group === 'misc'),
          ),
        ),
        position: 'main',
        labsSurface: true,
      },
      {
        label: 'Organization',
        icon: Building2,
        basePath: `${slugPrefix}/org`,
        tutorialId: 'nav-organization',
        // Core org-scoped rows stay listed without read (Members, Teams, …).
        // The page body is `HubReadGuard`; hiding the row is the old pattern.
        // Plugin contributions to this drill are still permission-filtered.
        items: applyBottomRoutes(
          dedup(
            coreOrganizationNavItems,
            filterByPerm(scopedPluginNavItems.filter((i) => i.group === 'organization')),
          ),
          [ROUTES.org(slug).settings],
        ),
        position: 'bottom',
        configureSurface: true,
      },
      {
        label: 'Integrations',
        icon: Plug,
        basePath: `${slugPrefix}/org/integrations`,
        parentBasePath: `${slugPrefix}/org`,
        items: dedup(
          coreIntegrationNavItems,
          filterByPerm(scopedPluginNavItems.filter((i) => i.group === 'integrations')),
        ),
        position: 'bottom',
        configureSurface: true,
      },
    ];
  }, [
    slug,
    scopedPluginNavItems,
    coreIntegrationNavItems,
    coreMiscNavItems,
    coreOrganizationNavItems,
    coreSecurityNavItems,
    filterByPerm,
  ]);

  const visibleDrillGroups = useMemo(
    () =>
      drillGroups.filter((g) => {
        if (!authEnabled) return true;
        if (g.requirePlatformAdmin && !isPlatformAdmin) return false;
        if (!g.permission) return true;
        if (isPlatformAdmin) return true;
        return can(g.permission);
      }),
    [drillGroups, authEnabled, isPlatformAdmin, can],
  );

  // Drill groups whose items are entirely plugin-driven (Plugins,
  // Integrations) collapse to a lone "Overview" link when no plugins
  // contribute. Hide them from the rendered sidebar in that state — the
  // entries stay in `visibleDrillGroups` so URL resolution and
  // `getActiveDrillGroup` keep working for users who navigate directly.
  // Configure-surface groups (Organization, Security, Integrations) render
  // inside the Configure takeover, not the sidebar or the mobile More sheet.
  const mainDrillGroups = useMemo(
    () =>
      visibleDrillGroups.filter(
        (g) =>
          g.position === 'main' &&
          !g.parentBasePath &&
          !g.configureSurface &&
          !g.labsSurface &&
          g.items.length > 0,
      ),
    [visibleDrillGroups],
  );
  const bottomDrillGroups = useMemo(
    () =>
      visibleDrillGroups.filter(
        (g) =>
          g.position === 'bottom' &&
          !g.parentBasePath &&
          !g.configureSurface &&
          !g.labsSurface &&
          g.items.length > 0,
      ),
    [visibleDrillGroups],
  );
  const configureDrillGroups = useMemo(
    () => visibleDrillGroups.filter((g) => g.configureSurface === true && g.items.length > 0),
    [visibleDrillGroups],
  );
  const labsDrillGroups = useMemo(
    () => visibleDrillGroups.filter((g) => g.labsSurface === true && g.items.length > 0),
    [visibleDrillGroups],
  );

  const pluginBottomItems = useMemo<NavItem[]>(
    () => filterByPerm(scopedPluginNavItems.filter((i) => i.group === 'bottom')),
    [scopedPluginNavItems, filterByPerm],
  );

  // The takeover doors, rendered above the Configure entry. Connect lands
  // on the credentials hub (first Connect page). Labs becomes an icon-only
  // entry next to Admin.
  const takeoverBottomItems = useMemo<NavItem[]>(() => {
    const items: NavItem[] = [];
    if (automationsItem) items.push(automationsItem);
    const connectTo = slug ? ROUTES.org(slug).credentialsHub : '/';
    items.push({ to: connectTo, label: 'Connect', icon: Cable, tutorialId: 'nav-connect' });
    return items;
  }, [automationsItem, slug]);

  // Labs is now an icon-only utility entry, positioned next to Admin.
  const labsItem = useMemo<NavItem>(() => {
    const to = labsChatItem?.to ?? withOrgSlug(slug, '/misc');
    return { to, label: 'Labs', icon: FlaskConical };
  }, [labsChatItem, slug]);

  // Admin is an icon-only entry in the sidebar utility row, not a labeled
  // door. Gated behind platform admin, same as the old drill group.
  const adminItem = useMemo<NavItem | null>(
    () =>
      !authEnabled || isPlatformAdmin
        ? { to: ROUTES.admin.root, label: 'Admin', icon: ShieldUser }
        : null,
    [authEnabled, isPlatformAdmin],
  );

  return {
    allMainNavItems,
    visibleDrillGroups,
    mainDrillGroups,
    bottomDrillGroups,
    configureDrillGroups,
    labsDrillGroups,
    labsChatItem,
    takeoverBottomItems,
    labsItem,
    adminItem,
    pluginBottomItems,
    scopedPluginNavItems,
  };
}
